import { Button, buttonClasses, Callout, Input, Skeleton } from '@fruuit/ui'
import { KeyRound } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useNavigate } from 'react-router'
import { errorMessage } from '../api/client'
import { useApiMutation } from '../api/hooks'
import { oauthStartUrl, requestSignInCode, useAuthProviders } from '../api/session'
import { GoogleIcon, MicrosoftIcon } from './ProviderIcons'

export interface SignInOptionsProps {
  /** Where to land after sign-in (validated same-origin path). */
  next: string | null
  /** Invitation token, forwarded to the auth flow. */
  invite?: string
}

/**
 * The three sign-in routes (P-ID-009). Visibility follows `GET /api/v1/auth/providers`:
 * a provider without credentials is hidden, never shown broken (P-ID-015).
 */
export function SignInOptions({ next, invite }: SignInOptionsProps) {
  const providers = useAuthProviders()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  // The code screen is the same either way; `method` only changes its wording (ADR-D08).
  const sendCode = useApiMutation(requestSignInCode, {
    onSuccess: (data, vars) => {
      const params = new URLSearchParams({ email: vars.email })
      if (data?.method === 'authenticator') params.set('method', 'authenticator')
      if (next) params.set('next', next)
      navigate(`/signin/code?${params.toString()}`)
    },
  })

  if (providers.isPending) {
    return (
      <div role="status" aria-label="Loading sign-in options" className="flex flex-col gap-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="mt-6 h-12 w-full" />
      </div>
    )
  }
  if (providers.isError) {
    return (
      <Callout tone="warning" role="alert">
        We couldn't load the sign-in options. Refresh the page to try again.
      </Callout>
    )
  }

  const { google, microsoft, email: emailEnabled } = providers.data
  if (!google && !microsoft && !emailEnabled) {
    return (
      <Callout tone="warning" role="alert">
        Sign-in is temporarily unavailable. Please try again a little later.
      </Callout>
    )
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    sendCode.mutate({ email: email.trim(), ...(invite ? { invite } : {}) })
  }

  const providerClass = buttonClasses({
    variant: 'secondary',
    size: 'lg',
    fullWidth: true,
    className: 'gap-3 text-ink no-underline',
  })

  return (
    <div className="flex flex-col gap-3">
      {microsoft ? (
        <a href={oauthStartUrl('microsoft', next, invite)} className={providerClass}>
          <MicrosoftIcon />
          Continue with Microsoft
        </a>
      ) : null}
      {google ? (
        <a href={oauthStartUrl('google', next, invite)} className={providerClass}>
          <GoogleIcon />
          Continue with Google
        </a>
      ) : null}

      {emailEnabled && (google || microsoft) ? (
        <div className="my-3 flex items-center gap-3 text-xs font-medium text-sage uppercase">
          <span aria-hidden="true" className="h-px flex-1 bg-line" />
          or
          <span aria-hidden="true" className="h-px flex-1 bg-line" />
        </div>
      ) : null}

      {emailEnabled ? (
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <Input
            label="Work email"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@company.com"
            error={sendCode.error ? errorMessage(sendCode.error) : undefined}
          />
          <Button
            type="submit"
            size="lg"
            fullWidth
            icon={<KeyRound />}
            loading={sendCode.isPending}
          >
            Sign in
          </Button>
          <p className="text-[13px] leading-relaxed text-sage">
            We email you a one-time code, or ask for the code from your authenticator app if you
            have set one up.
          </p>
        </form>
      ) : null}
    </div>
  )
}
